import {
  localizedContactSchema,
  localizedFaqSchema,
  localizedGetStartedSchema,
  localizedHomeSchema,
  localizedProductSchema,
  localizedSettingsSchema,
  type ContactContent,
  type FaqContent,
  type GetStartedContent,
  type HomeContent,
  type Locale,
  type ProductContent,
  type Settings,
} from '@shared/content-schemas';
import { defaultLocale, resolveLocale } from '@shared/locales';
import settingsJson from '../../../content/settings.json';
import homeJson from '../../../content/pages/home.json';
import productJson from '../../../content/pages/product.json';
import getStartedJson from '../../../content/pages/get-started.json';
import contactJson from '../../../content/pages/contact.json';
import faqJson from '../../../content/pages/faq.json';

/**
 * Site content, loaded from ./content at build time and validated against
 * src/shared/content-schemas.ts on import. A CMS edit that breaks a schema
 * fails the tests and the build (the prerender imports this module) before it
 * can deploy.
 */
const settingsByLocale = localizedSettingsSchema.parse(settingsJson);
const homeByLocale = localizedHomeSchema.parse(homeJson);
const productByLocale = localizedProductSchema.parse(productJson);
const getStartedByLocale = localizedGetStartedSchema.parse(getStartedJson);
const contactByLocale = localizedContactSchema.parse(contactJson);
const faqByLocale = localizedFaqSchema.parse(faqJson);

function forLocale<T>(localized: Record<Locale, T>, lang: string | undefined): T {
  return localized[resolveLocale(lang)] ?? localized[defaultLocale];
}

export const getSettings = (lang: string): Settings => forLocale(settingsByLocale, lang);
export const getHomeContent = (lang: string): HomeContent => forLocale(homeByLocale, lang);
export const getProductContent = (lang: string): ProductContent => forLocale(productByLocale, lang);
export const getGetStartedContent = (lang: string): GetStartedContent => forLocale(getStartedByLocale, lang);
export const getContactContent = (lang: string): ContactContent => forLocale(contactByLocale, lang);
export const getFaqContent = (lang: string): FaqContent => forLocale(faqByLocale, lang);

/** Splits plain text into paragraphs on blank lines. */
export function paragraphs(text: string): string[] {
  return text
    .split(/\n\s*\n/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);
}
